import { format, isValid, parse, set } from "date-fns";
import { shortenTimezone } from "./timezones";

export function formatDateTime(
  date: Date,
  dateFormat: DateFormats,
  timezone: string | null | undefined,
  timeFormat: TimeFormatOptions = "default",
) {
  if (!isValid(date)) return "-";
  const dateString = formatDate(date, dateFormat);
  const timeString = formatTime(date, timeFormat);
  const tzString = timezone ? ` (${shortenTimezone(timezone)})` : "";
  return `${dateString}, ${timeString}${tzString}`;
}

export function formatDateTimeRange(
  start: Date,
  end: Date,
  dateFormat: DateFormats,
  timezone: string | null | undefined,
  timeFormat: TimeFormatOptions = "default",
) {
  if (!isValid(start) || !isValid(end)) return "-";
  const tzString = timezone ? ` (${shortenTimezone(timezone)})` : "";
  return `${formatDate(start, dateFormat)}, ${formatTime(start, timeFormat)} - ${formatTime(end, timeFormat)}${tzString}`;
}

// Keep tight — only add a variant when no existing one fits.
type DateFormats = "EEE" | "EEEE" | "EEEE, MMM d" | "EEE, MMM d" | "MMM d" | "MM/dd/yyyy" | "MMM d, yyyy" | "MMM yyyy";

export function formatDate(date: Date | number, pattern: DateFormats) {
  if (!isValid(date)) return "-";
  return format(date, pattern);
}

const timeFormatLookup = {
  backend: "HH:mm:ss",
  default: "h:mm aaa",
  detailed: "hh:mm:ss aaa",
} as const;

type TimeFormatOptions = keyof typeof timeFormatLookup;

export const parseTime = (
  time: string,
  formatString: TimeFormatOptions = "default",
  referenceDate: Date = new Date(),
): Date => parse(time, timeFormatLookup[formatString], referenceDate);

export const formatTime = (time: Date, formatString: TimeFormatOptions = "default") =>
  isValid(time) ? format(time, timeFormatLookup[formatString]) : "-";

export const reformatTime = (time: string, format: { in: TimeFormatOptions; out: TimeFormatOptions }) =>
  formatTime(parseTime(time, format.in), format.out);

/** Put the time-of-day from `time` onto the calendar day of `date`. */
export const combineDateAndTime = (date: Date, time: Date): Date =>
  set(date, { hours: time.getHours(), minutes: time.getMinutes(), seconds: time.getSeconds(), milliseconds: 0 });

/**
 * Serialize a `Date` to a date-only "YYYY-MM-DD" wire value (UTC).
 * Avoids the timezone shift `JSON.stringify` causes by emitting a full datetime.
 */
export const toDateOnly = (date: Date): string => {
  const iso = date.toISOString();
  return iso.slice(0, iso.indexOf("T"));
};

/**
 * Inverse of `toDateOnly`: the API client revives a "YYYY-MM-DD" wire value as UTC midnight, which is the
 * previous calendar day for anyone west of UTC. Returns local midnight of that same calendar day so
 * `formatDate` and friends show the day the backend stored.
 */
export const fromDateOnly = (date: Date): Date =>
  new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

/** Local-day formatter for a picker value already zoned to the day the user saw, so it ships as that day
 *  rather than `toDateOnly`'s UTC day. Use as `toWire` for `toDateOnlyFields`. */
export const toLocalDateOnly = (date: Date) => format(date, "yyyy-MM-dd");
